import { useState, useEffect, useCallback } from "react";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
}

interface CapacitorGlobal { isNativePlatform?: () => boolean; getPlatform?: () => string; }
interface NavigatorWithStandalone extends Navigator { standalone?: boolean; }
interface WindowWithCapacitor extends Window { Capacitor?: CapacitorGlobal; MSStream?: unknown; }

export function usePWA() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const windowWithCapacitor = window as WindowWithCapacitor;
    const isCapacitorNative = !!(windowWithCapacitor.Capacitor?.isNativePlatform?.());
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    const navigatorWithStandalone = window.navigator as NavigatorWithStandalone;
    const isIOSStandalone = navigatorWithStandalone.standalone === true;
    setIsInstalled(isStandalone || isIOSStandalone || isCapacitorNative);

    const windowWithMSStream = window as WindowWithCapacitor;
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !windowWithMSStream.MSStream;
    setIsIOS(isIOSDevice);

    if (isCapacitorNative) { setIsInstallable(false); return; }

    const handleBeforeInstallPrompt = (e: Event) => { e.preventDefault(); setDeferredPrompt(e as BeforeInstallPromptEvent); setIsInstallable(true); };
    const handleAppInstalled = () => { setIsInstalled(true); setIsInstallable(false); setDeferredPrompt(null); };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const installApp = useCallback(async () => {
    if (!deferredPrompt) return false;
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") { setIsInstalled(true); setIsInstallable(false); }
      setDeferredPrompt(null);
      return outcome === "accepted";
    } catch (error) { console.error("Error installing PWA:", error instanceof Error ? error.message : String(error)); return false; }
  }, [deferredPrompt]);

  return { isInstallable, isInstalled, isIOS, installApp };
}